import * as React from 'react'
import { ScrollRestoration, ShouldRevalidateFunction, useSearchParams } from 'react-router-dom'

import { shouldRevalidateOnQueryChange } from '../../util-routing'
import { LazyRouteDefinition, createRouteViewSwitch, lazyLoadComponent } from '../../util-routing'
import { QuoteQuery, QuoteSubview } from './types'
import { getQuoteSubview, getQuoteSubviewFromQuery } from './utils'

const QuoteSharedWrapper = lazyLoadComponent({
  chunkName: 'quote-portal-components',
  load: () => import(/* webpackChunkName: "quote-portal-components" */ './components/QuoteSharedWrapper'),
})

export function getShouldRevalidateQuoteRoute(): ShouldRevalidateFunction {
  return (args) => {
    const route = getQuoteSubview(args.currentUrl.searchParams.get('ty') as QuoteSubview)

    switch (route) {
      case QuoteSubview.OptionsChain:
      case QuoteSubview.OptionsVolatility:
        return shouldRevalidateOnQueryChange(args, ['e', 's', 'ov'])

      default:
        return false
    }
  }
}

const optionsBundle = {
  loader: () => import(/* webpackChunkName: "options-loaders" */ '../options/loaders'),
  Component: lazyLoadComponent({
    chunkName: 'options',
    load: () => import(/* webpackChunkName: "options" */ '../options'),
  }),
}

const { loader, action, ViewSwitch } = createRouteViewSwitch({
  defaultView: '',
  viewParam: QuoteQuery.Type,
  views: {
    [QuoteSubview.OptionsChain]: optionsBundle,
    [QuoteSubview.OptionsVolatility]: optionsBundle,
    [QuoteSubview.Revenue]: {
      Component: lazyLoadComponent({
        chunkName: 'revenue',
        load: () => import(/* webpackChunkName: "revenue" */ '../revenue'),
      }),
    },
    [QuoteSubview.Dividends]: {
      Component: lazyLoadComponent({
        chunkName: 'dividends',
        load: () => import(/* webpackChunkName: "dividends" */ '../dividends'),
      }),
    },
    [QuoteSubview.Eearnings]: {
      Component: lazyLoadComponent({
        chunkName: 'earnings',
        load: () => import(/* webpackChunkName: "earnings" */ '../earnings'),
      }),
    },
    [QuoteSubview.Forecast]: {
      Component: lazyLoadComponent({
        chunkName: 'forecast',
        load: () => import(/* webpackChunkName: "forecast" */ '../forecast'),
      }),
    },
    [QuoteSubview.ShortInterest]: {
      Component: lazyLoadComponent({
        chunkName: 'short-interest',
        load: () => import(/* webpackChunkName: "short-interest" */ '../short-interest'),
      }),
    },
    [QuoteSubview.Traffic]: {
      Component: lazyLoadComponent({
        chunkName: 'traffic',
        load: () => import(/* webpackChunkName: "traffic" */ '../traffic'),
      }),
    },
    [QuoteSubview.LatestFilings]: {
      Component: lazyLoadComponent({
        chunkName: 'latest-filings',
        load: () => import(/* webpackChunkName: "latest-filings" */ '../latest-filings'),
      }),
    },
  },
})

export const quoteModule: LazyRouteDefinition[] = [
  {
    path: '/quote.ashx',
    loader,
    action,
    shouldRevalidate: getShouldRevalidateQuoteRoute(),
    Component: function QuoteRoot() {
      const [searchParams] = useSearchParams()
      const subview = getQuoteSubviewFromQuery(searchParams)

      React.useEffect(() => {
        if (FinvizSettings.hasUserPremium || FinvizSettings.hasRedesignEnabled) {
          import(/* webpackChunkName: "recent_quotes" */ '../../../app/shared/recent_quotes')
            .then(({ RenderRecentQuotes }) => {
              RenderRecentQuotes()
            })
            .catch()
        }
      }, [])

      return (
        <>
          {/* Intentionally do not trigger upper router loading boundary, these components use portals */}
          <React.Suspense fallback={null}>
            <QuoteSharedWrapper />
          </React.Suspense>

          <ViewSwitch />

          {/**
           * We precompute the element height on the backend so that the content under
           * options doesn’t jump when options render, so we can put restore scroll here.
           */}
          {subview && <ScrollRestoration />}
        </>
      )
    },
  },
]
